import React, { useEffect, useState, useCallback } from 'react';
import { todayIso, toIsoDate, fmtDateTimeMedium } from '../utils/dateUtils';
import { exportToPdf } from '../utils/exportToPdf';
import { exportToCsv, exportToExcel } from '../utils/exportData';
import useIsMobile from '../hooks/useIsMobile';

// REQ-08: shared column defs for the Night Audit log export (PDF / CSV / Excel).
const NIGHT_AUDIT_EXPORT_COLUMNS = [
  { header: 'Business Date', dataKey: 'business_date' },
  { header: 'Triggered', dataKey: 'triggered_by' },
  { header: 'Run By', dataKey: 'run_by' },
  { header: 'Status', dataKey: 'status' },
  { header: 'No-Shows', dataKey: 'no_shows', halign: 'right' },
  { header: 'Occupancy %', dataKey: 'occupancy_pct', halign: 'right' },
  { header: 'Revenue', dataKey: 'revenue', halign: 'right' },
  { header: 'Duration (ms)', dataKey: 'duration_ms', halign: 'right' },
  { header: 'Completed', dataKey: 'completed' },
];

const fmtNum = (v, d = 2) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtDt = (v) => fmtDateTimeMedium(v);
const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toIsoDate(d);
};

const STEP_LABELS = {
  PRE_VALIDATION: 'Pre-Validation',
  NO_SHOW_PROCESSING: 'No-Show Processing',
  ROOM_CHARGE_POSTING: 'Room Charge Posting',
  SNAPSHOT_CAPTURE: 'Snapshot Capture',
  HOUSEKEEPING_FLIP: 'Housekeeping Flip',
  BUSINESS_DATE_ROLL: 'Business Date Roll',
  INVENTORY_RECALC: 'Inventory Recalc',
  FINALIZE: 'Finalize',
};

function PmsNightAudit({ token, selectedProperty, role, permissions }) {
  // Night Audit email settings are Hotel Admin / Super Admin only (API enforces it too).
  const canManageEmail = ['admin', 'hotel_admin', 'hotel admin'].includes(String(role || '').toLowerCase()) || Boolean(permissions?.hotel_admin);
  const { isMobile } = useIsMobile();
  const [tab, setTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotDate, setSnapshotDate] = useState(yesterdayIso());
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [confirmRun, setConfirmRun] = useState(false);
  const [runDate, setRunDate] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailForm, setEmailForm] = useState({ enabled: false, recipients: '', send_on_failure: true });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [emailSendDate, setEmailSendDate] = useState(yesterdayIso());

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  // Load status
  const loadStatus = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/night-audit/status', { headers: headers() });
      const data = await res.json();
      if (res.ok) setStatus(data);
    } catch (err) { /* ignore */ }
  }, [selectedProperty, headers]);

  // Load snapshot for a date
  const loadSnapshot = useCallback(async (date) => {
    if (!selectedProperty?.id || !date) return;
    setSnapshot(null);
    try {
      const res = await fetch(`/api/v1/night-audit/snapshot/${date}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setSnapshot(data);
      }
    } catch (err) { /* ignore */ }
  }, [selectedProperty, headers]);

  // Load history
  const loadHistory = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      let url = '/api/v1/night-audit/log?limit=30';
      if (historyFrom) url += `&from=${historyFrom}`;
      if (historyTo) url += `&to=${historyTo}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (res.ok) {
        setHistory(data.data || []);
        setHistoryTotal(data.total || 0);
      }
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, [selectedProperty, headers, historyFrom, historyTo]);

  useEffect(() => {
    setError('');
    setRunResult(null);
    setConfirmRun(false);
    loadStatus();
    loadSnapshot(snapshotDate);
  }, [selectedProperty?.id, token, loadStatus, loadSnapshot, snapshotDate]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // Run night audit
  const doRun = async () => {
    setConfirmRun(false);
    setRunning(true);
    setRunResult(null);
    setError('');
    try {
      const body = runDate ? JSON.stringify({ business_date: runDate }) : undefined;
      const res = await fetch('/api/v1/night-audit/run', { method: 'POST', headers: headers(), body });
      const data = await res.json();
      if (res.status === 409) {
        setError(data.error || 'Night audit already completed for this date');
      } else if (!res.ok) {
        setError(data.error || 'Night audit failed');
      }
      setRunResult(data);
      loadStatus();
      loadSnapshot(snapshotDate);
    } catch (err) {
      setError(err.message || 'Network error');
    }
    setRunning(false);
  };

  const applyEmailCfg = (cfg) => {
    setEmailCfg(cfg);
    setEmailForm({ enabled: Boolean(cfg?.enabled), recipients: (cfg?.recipients || []).join('\n'), send_on_failure: cfg?.send_on_failure !== false });
  };
  useEffect(() => {
    if (tab !== 'email' || !canManageEmail || !selectedProperty?.id) return;
    setEmailErr(''); setEmailMsg('');
    fetch('/api/v1/night-audit/email-config', { headers: headers() })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to load email settings');
        applyEmailCfg(data);
      })
      .catch((err) => setEmailErr(err.message));
  }, [tab, canManageEmail, selectedProperty, headers]);

  const saveEmailCfg = async () => {
    setEmailBusy(true); setEmailErr(''); setEmailMsg('');
    try {
      const res = await fetch('/api/v1/night-audit/email-config', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ enabled: emailForm.enabled, recipients: emailForm.recipients, send_on_failure: emailForm.send_on_failure })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      applyEmailCfg(data);
      setEmailMsg(data.enabled ? 'Saved — the report will be emailed after every Night Audit.' : 'Saved — Night Audit email is switched off.');
    } catch (err) { setEmailErr(err.message); }
    finally { setEmailBusy(false); }
  };

  const sendEmailNow = async () => {
    setEmailBusy(true); setEmailErr(''); setEmailMsg('');
    try {
      const res = await fetch('/api/v1/night-audit/email-send', {
        method: 'POST', headers: headers(), body: JSON.stringify({ business_date: emailSendDate })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Email could not be sent');
      setEmailMsg(`Report for ${emailSendDate} sent to the saved recipients.`);
      const r2 = await fetch('/api/v1/night-audit/email-config', { headers: headers() });
      if (r2.ok) applyEmailCfg(await r2.json());
    } catch (err) { setEmailErr(err.message); }
    finally { setEmailBusy(false); }
  };

  const tabBtn = (active) => ({
    padding: '0.55rem 1rem',
    borderRadius: '7px',
    border: '1px solid #cbd5e0',
    background: active ? 'var(--theme-primary)' : '#fff',
    color: active ? '#fff' : '#1f2937',
    cursor: 'pointer',
    fontWeight: active ? 700 : 400,
  });
  // Themed primary action button (Bug 8679 — the Load button was rendering as an
  // unstyled default browser button, out of place next to the styled tabs).
  const primaryBtn = {
    padding: '0.45rem 1.1rem',
    borderRadius: '7px',
    border: 'none',
    background: 'var(--theme-primary)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
  };
  const cardStyle = { background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)', padding: '1rem', textAlign: 'center' };
  const kpiVal = { fontSize: '1.5rem', fontWeight: 700, color: 'var(--theme-primary)' };
  const kpiLabel = { fontSize: '0.8rem', color: 'var(--theme-text-secondary)', marginTop: '0.25rem' };
  const thStyle = { padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', fontSize: '0.82rem', fontWeight: 600 };
  const tdStyle = { padding: '0.5rem', borderBottom: '1px solid var(--theme-border-light)', fontSize: '0.85rem' };

  const statusBadge = (s) => {
    const colors = { COMPLETED: 'var(--theme-success)', FAILED: 'var(--theme-danger)', RUNNING: '#2563eb', PARTIAL: '#f59e0b', ALREADY_COMPLETED: 'var(--theme-text-secondary)', BLOCKED: '#dc2626' };
    return { display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, color: '#fff', background: colors[s] || 'var(--theme-text-muted)' };
  };

  // REQ-08: export the Night Audit log as PDF / CSV / Excel from the loaded history.
  const exportHistory = (fmt) => {
    if (!history.length) { setError('Nothing to export — load history first.'); return; }
    const rows = history.map((h) => ({
      business_date: h.business_date || '',
      triggered_by: h.triggered_by || '',
      run_by: h.run_by_username || '',
      status: h.status || '',
      no_shows: h.stats?.no_shows_count ?? '',
      occupancy_pct: h.stats?.occupancy_percent != null ? fmtNum(h.stats.occupancy_percent, 1) : '',
      revenue: h.stats?.total_revenue != null ? fmtNum(h.stats.total_revenue) : '',
      duration_ms: h.duration_ms ?? '',
      completed: h.completed_at ? fmtDt(h.completed_at) : '',
    }));
    const fname = `night_audit_log_${historyFrom || 'all'}_to_${historyTo || 'all'}`;
    if (fmt === 'pdf') {
      exportToPdf({
        title: 'Night Audit Log',
        subtitle: selectedProperty?.name || '',
        meta: [`${historyTotal} record(s)`, `Generated: ${fmtDt(new Date())}`],
        sections: [{ columns: NIGHT_AUDIT_EXPORT_COLUMNS, rows }],
        filename: `${fname}.pdf`,
        orientation: 'landscape',
        footerLeft: selectedProperty?.name || '',
      });
    } else if (fmt === 'csv') {
      exportToCsv(fname, NIGHT_AUDIT_EXPORT_COLUMNS, rows);
    } else {
      exportToExcel(fname, NIGHT_AUDIT_EXPORT_COLUMNS, rows, 'Night Audit Log');
    }
  };

  const renderKPIs = (s) => {
    if (!s) return null;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.8rem', marginBottom: '1rem' }}>
        <div style={cardStyle}><div style={kpiVal}>{fmtNum(s.occupancy_percent || s.occupancyPercent, 1)}%</div><div style={kpiLabel}>Occupancy</div></div>
        <div style={cardStyle}><div style={kpiVal}>{fmtNum(s.adr)}</div><div style={kpiLabel}>ADR</div></div>
        <div style={cardStyle}><div style={kpiVal}>{fmtNum(s.revpar)}</div><div style={kpiLabel}>RevPAR</div></div>
        <div style={cardStyle}><div style={kpiVal}>{fmtNum(s.total_revenue || s.totalRevenue)}</div><div style={kpiLabel}>Total Revenue</div></div>
        <div style={cardStyle}><div style={kpiVal}>{fmtInt(s.rooms_occupied || s.roomsOccupied)}</div><div style={kpiLabel}>Rooms Occupied</div></div>
        <div style={cardStyle}><div style={kpiVal}>{fmtInt(s.rooms_vacant || s.roomsVacant)}</div><div style={kpiLabel}>Rooms Vacant</div></div>
      </div>
    );
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Night Audit</h2>
      {error ? <div className="error-banner">{error}</div> : null}

      {/* Status bar */}
      {status && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.7rem 1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', fontSize: '0.88rem' }}>
          <span><strong>Business Date:</strong> {status.business_date || '-'}</span>
          {status.last_audit ? (
            <>
              <span><strong>Last Audit:</strong> {status.last_audit.business_date} — <span style={statusBadge(status.last_audit.status)}>{status.last_audit.status}</span></span>
              <span><strong>Run:</strong> {fmtDt(status.last_audit.completed_at || status.last_audit.started_at)} ({status.last_audit.triggered_by})</span>
            </>
          ) : <span style={{ color: 'var(--theme-text-secondary)' }}>No audit run yet</span>}
          <span><strong>Auto:</strong> {status.night_audit_auto_enabled ? `Enabled at ${status.night_audit_auto_time}` : 'Disabled'}</span>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button onClick={() => setTab('overview')} style={tabBtn(tab === 'overview')}>Overview</button>
        <button onClick={() => setTab('run')} style={tabBtn(tab === 'run')}>Run Audit</button>
        <button onClick={() => setTab('history')} style={tabBtn(tab === 'history')}>History</button>
        {canManageEmail && <button onClick={() => setTab('email')} style={tabBtn(tab === 'email')}>Email Report</button>}
      </div>

      {/* ── Overview Tab ── */}
      {tab === 'overview' && (
        <div>
          <div style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Snapshot Date:</label>
            <input type="date" value={snapshotDate} onChange={(e) => setSnapshotDate(e.target.value)} style={{ padding: '0.3rem 0.5rem' }} />
            <button onClick={() => loadSnapshot(snapshotDate)} style={primaryBtn}>Load</button>
          </div>

          {snapshot ? (
            <>
              {renderKPIs(snapshot)}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                {/* Movements */}
                <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)', padding: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.6rem', color: 'var(--theme-primary)' }}>Movements</h4>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr><td style={tdStyle}>Arrivals (Expected / Actual)</td><td style={{ ...tdStyle, fontWeight: 600 }}>{snapshot.arrivals_expected} / {snapshot.arrivals_actual}</td></tr>
                      <tr><td style={tdStyle}>Departures (Expected / Actual)</td><td style={{ ...tdStyle, fontWeight: 600 }}>{snapshot.departures_expected} / {snapshot.departures_actual}</td></tr>
                      <tr><td style={tdStyle}>No-Shows</td><td style={{ ...tdStyle, fontWeight: 600, color: snapshot.no_shows_count > 0 ? 'var(--theme-danger)' : undefined }}>{snapshot.no_shows_count}</td></tr>
                      <tr><td style={tdStyle}>Walk-Ins</td><td style={{ ...tdStyle, fontWeight: 600 }}>{snapshot.walk_ins}</td></tr>
                      <tr><td style={tdStyle}>Stayovers</td><td style={{ ...tdStyle, fontWeight: 600 }}>{snapshot.stayovers}</td></tr>
                      <tr><td style={tdStyle}>Guests In-House</td><td style={{ ...tdStyle, fontWeight: 600 }}>{snapshot.total_guests_in_house}</td></tr>
                      <tr><td style={tdStyle}>Rooms OOO</td><td style={{ ...tdStyle, fontWeight: 600 }}>{snapshot.rooms_ooo}</td></tr>
                    </tbody>
                  </table>
                </div>

                {/* Revenue */}
                <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)', padding: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.6rem', color: 'var(--theme-primary)' }}>Revenue Breakdown</h4>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr><td style={tdStyle}>Room Tariff</td><td style={{ ...tdStyle, fontWeight: 600, textAlign: 'right' }}>{fmtNum(snapshot.room_revenue)}</td></tr>
                      <tr><td style={tdStyle}>F&B (KOT + Bar)</td><td style={{ ...tdStyle, fontWeight: 600, textAlign: 'right' }}>{fmtNum(snapshot.fnb_revenue)}</td></tr>
                      <tr><td style={tdStyle}>Other Services</td><td style={{ ...tdStyle, fontWeight: 600, textAlign: 'right' }}>{fmtNum(snapshot.other_revenue)}</td></tr>
                      <tr style={{ background: '#f0f9ff' }}><td style={{ ...tdStyle, fontWeight: 700 }}>Total Revenue</td><td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>{fmtNum(snapshot.total_revenue)}</td></tr>
                      <tr><td style={tdStyle}>Total Tax</td><td style={{ ...tdStyle, textAlign: 'right' }}>{fmtNum(snapshot.total_tax)}</td></tr>
                      <tr><td style={tdStyle}>Payments Received</td><td style={{ ...tdStyle, fontWeight: 600, textAlign: 'right', color: 'var(--theme-success)' }}>{fmtNum(snapshot.total_payments)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* By Room Type */}
              {snapshot.by_room_type && snapshot.by_room_type.length > 0 && (
                <div style={{ marginTop: '1rem', background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)', padding: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.6rem', color: 'var(--theme-primary)' }}>By Room Type</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--theme-bg-light)' }}>
                        <th style={thStyle}>Room Type</th>
                        <th style={thStyle}>Total</th>
                        <th style={thStyle}>Occupied</th>
                        <th style={thStyle}>Vacant</th>
                        <th style={thStyle}>Occupancy %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshot.by_room_type.map((rt, i) => (
                        <tr key={i}>
                          <td style={tdStyle}>{rt.room_type_name}</td>
                          <td style={tdStyle}>{rt.total}</td>
                          <td style={tdStyle}>{rt.occupied}</td>
                          <td style={tdStyle}>{rt.vacant}</td>
                          <td style={tdStyle}>{fmtNum(rt.occupancy_pct, 1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-secondary)', background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)' }}>
              No snapshot available for {snapshotDate}. Run Night Audit to generate data.
            </div>
          )}
        </div>
      )}

      {/* ── Run Audit Tab ── */}
      {tab === 'run' && (
        <div>
          <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)', padding: '1.5rem', maxWidth: '600px' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Business Date to Close:</span>
              <input
                type="date"
                value={runDate || status?.business_date || ''}
                onChange={(e) => setRunDate(e.target.value)}
                style={{ padding: '0.35rem 0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--theme-primary)', border: '1px solid #cbd5e0', borderRadius: '5px' }}
              />
              {runDate && runDate !== status?.business_date && (
                <span style={{ fontSize: '0.78rem', color: '#b45309', background: '#fef3c7', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>
                  Override — default is {status?.business_date}
                </span>
              )}
            </div>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>
              This will process no-shows, capture a daily snapshot, flip housekeeping, and roll the business date forward.
            </div>

            {!confirmRun && !runResult && (
              <button
                onClick={() => setConfirmRun(true)}
                disabled={running}
                style={{ padding: '0.6rem 1.5rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}
              >
                Run Night Audit
              </button>
            )}

            {confirmRun && (
              <div style={{ padding: '0.8rem', background: '#fefce8', border: '1px solid #fde047', borderRadius: '6px', marginTop: '0.5rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
                  Confirm: Close business date {runDate || status?.business_date} and roll to next day?
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={doRun} style={{ padding: '0.4rem 1rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: 600, cursor: 'pointer' }}>
                    Confirm & Run
                  </button>
                  <button onClick={() => setConfirmRun(false)} style={{ padding: '0.4rem 1rem', background: 'var(--theme-border)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {running && (
              <div style={{ marginTop: '0.8rem', color: '#2563eb', fontWeight: 600 }}>Running night audit... please wait.</div>
            )}
          </div>

          {/* Run result */}
          {runResult && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>Result: </span>
                <span style={statusBadge(runResult.status)}>{runResult.status}</span>
                {runResult.new_business_date && (
                  <span style={{ marginLeft: '1rem', fontSize: '0.88rem' }}>
                    New Business Date: <strong>{runResult.new_business_date}</strong>
                  </span>
                )}
              </div>

              {/* Steps table */}
              {runResult.steps && runResult.steps.length > 0 && (
                <table style={{ width: '100%', maxWidth: '700px', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', border: '1px solid var(--theme-border)' }}>
                  <thead>
                    <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Step</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Detail</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runResult.steps.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                        <td style={{ padding: '0.45rem' }}>{STEP_LABELS[s.step] || s.step}</td>
                        <td style={{ padding: '0.45rem' }}><span style={statusBadge(s.status)}>{s.status}</span></td>
                        <td style={{ padding: '0.45rem', fontSize: '0.82rem', color: '#475569' }}>{s.detail}</td>
                        <td style={{ padding: '0.45rem', textAlign: 'right', fontSize: '0.82rem' }}>{s.duration_ms != null ? `${s.duration_ms}ms` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Summary KPIs from run result stats */}
              {runResult.stats && runResult.stats.total_rooms > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: 'var(--theme-primary)', margin: '0 0 0.5rem' }}>Snapshot Summary</h4>
                  {renderKPIs(runResult.stats)}
                </div>
              )}

              <button
                onClick={async () => {
                  if (runResult?.status === 'ALREADY_COMPLETED' || runResult?.status === 'COMPLETED') {
                    try {
                      const res = await fetch('/api/v1/night-audit/reset', {
                        method: 'POST', headers: headers(),
                        body: JSON.stringify({ business_date: runDate || status?.business_date })
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) { setError(data.error || 'Reset failed'); return; }
                    } catch (e) { setError(e.message); return; }
                  }
                  setRunResult(null); setConfirmRun(false); setError(''); loadStatus();
                }}
                style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', background: 'var(--theme-border)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.8rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>From:</label>
              <input type="date" value={historyFrom} onChange={(e) => setHistoryFrom(e.target.value)} style={{ padding: '0.3rem 0.5rem' }} />
              <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>To:</label>
              <input type="date" value={historyTo} onChange={(e) => setHistoryTo(e.target.value)} style={{ padding: '0.3rem 0.5rem' }} />
              <button onClick={loadHistory} style={primaryBtn}>Load</button>
              <span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>{historyTotal} record(s)</span>
              {history.length > 0 && (
                <span style={{ display: 'inline-flex', gap: '0.3rem', marginLeft: '0.4rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>Export:</span>
                  <button onClick={() => exportHistory('pdf')} style={{ padding: '0.3rem 0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '5px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, color: '#334155' }} title="Export as PDF">PDF</button>
                  <button onClick={() => exportHistory('csv')} style={{ padding: '0.3rem 0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '5px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, color: '#334155' }} title="Export as CSV">CSV</button>
                  <button onClick={() => exportHistory('excel')} style={{ padding: '0.3rem 0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '5px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, color: '#334155' }} title="Export as Excel">Excel</button>
                </span>
              )}
            </div>
            <button
              disabled={purging}
              onClick={async () => {
                const today = todayIso();
                const bizDate = status?.business_date || today;
                // Use the EARLIER of today or business_date as cutoff — this handles
                // the runaway case where business_date is far in the future
                const cutoff = bizDate > today ? today : bizDate;
                if (!window.confirm(`This will delete all audit logs and snapshots for dates >= ${cutoff} and reset the business date to ${cutoff}. Continue?`)) return;
                setPurging(true); setPurgeResult(null);
                try {
                  const res = await fetch('/api/v1/night-audit/purge-future', { method: 'POST', headers: headers(), body: JSON.stringify({ cutoff_date: cutoff, reset_business_date: true }) });
                  const data = await res.json();
                  if (res.ok) {
                    setPurgeResult(`Purged ${data.logs_deleted} log(s) and ${data.snapshots_deleted} snapshot(s) from ${data.cutoff_date} onwards.${data.business_date_reset ? ' Business date reset to ' + data.new_business_date + '.' : ''}`);
                    loadHistory(); loadStatus();
                  } else {
                    setPurgeResult(`Error: ${data.error}`);
                  }
                } catch (e) { setPurgeResult(`Error: ${e.message}`); }
                setPurging(false);
              }}
              style={{ padding: '0.4rem 1rem', background: 'var(--theme-danger)', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700 }}
            >
              {purging ? 'Purging...' : 'Purge Future Audits'}
            </button>
          </div>
          {purgeResult && (
            <div style={{ marginBottom: '0.8rem', padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
              background: purgeResult.startsWith('Error') ? '#fef2f2' : '#f0fdf4',
              color: purgeResult.startsWith('Error') ? 'var(--theme-danger)' : 'var(--theme-success)',
              border: `1px solid ${purgeResult.startsWith('Error') ? '#fecaca' : '#bbf7d0'}` }}>
              {purgeResult}
            </div>
          )}

          {loading && <div style={{ color: '#2563eb' }}>Loading...</div>}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Triggered</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>By</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>No-Shows</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Occ %</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Revenue</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Duration</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Completed</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const s = h.stats || {};
                  const isExpanded = expandedRow === h.audit_log_id;
                  return (
                    <React.Fragment key={h.audit_log_id}>
                      <tr
                        style={{ borderBottom: '1px solid var(--theme-border)', cursor: 'pointer', background: isExpanded ? '#f0f9ff' : undefined }}
                        onClick={() => setExpandedRow(isExpanded ? null : h.audit_log_id)}
                      >
                        <td style={{ padding: '0.45rem', fontWeight: 600 }}>{h.business_date}</td>
                        <td style={{ padding: '0.45rem' }}>{h.triggered_by}</td>
                        <td style={{ padding: '0.45rem' }}>{h.run_by_username || '-'}</td>
                        <td style={{ padding: '0.45rem' }}><span style={statusBadge(h.status)}>{h.status}</span></td>
                        <td style={{ padding: '0.45rem', textAlign: 'right' }}>{s.no_shows_count ?? '-'}</td>
                        <td style={{ padding: '0.45rem', textAlign: 'right' }}>{s.occupancy_percent != null ? `${fmtNum(s.occupancy_percent, 1)}%` : '-'}</td>
                        <td style={{ padding: '0.45rem', textAlign: 'right' }}>{s.total_revenue != null ? fmtNum(s.total_revenue) : '-'}</td>
                        <td style={{ padding: '0.45rem', textAlign: 'right' }}>{h.duration_ms != null ? `${h.duration_ms}ms` : '-'}</td>
                        <td style={{ padding: '0.45rem', fontSize: '0.82rem' }}>{fmtDt(h.completed_at)}</td>
                        <td style={{ padding: '0.45rem', textAlign: 'center' }}>
                          {h.status === 'COMPLETED' && (
                            <button
                              disabled={resettingId === h.audit_log_id}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Reset completed audit for ${h.business_date}? This will allow re-running the night audit for this date.`)) return;
                                setResettingId(h.audit_log_id);
                                try {
                                  const res = await fetch('/api/v1/night-audit/reset', {
                                    method: 'POST', headers: headers(),
                                    body: JSON.stringify({ business_date: h.business_date })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (res.ok) {
                                    loadHistory(); loadStatus();
                                  } else {
                                    setError(data.error || 'Reset failed');
                                  }
                                } catch (err) { setError(err.message); }
                                setResettingId(null);
                              }}
                              style={{ padding: '0.2rem 0.6rem', background: '#b45309', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                            >
                              {resettingId === h.audit_log_id ? 'Resetting...' : 'Reset'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} style={{ padding: '0.8rem', background: 'var(--theme-bg-light)', borderBottom: '2px solid var(--theme-border)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              {/* Steps */}
                              <div>
                                <strong style={{ fontSize: '0.85rem' }}>Steps:</strong>
                                <table style={{ width: '100%', marginTop: '0.3rem', fontSize: '0.82rem' }}>
                                  <tbody>
                                    {(h.steps_completed || []).map((st, i) => (
                                      <tr key={i}>
                                        <td style={{ padding: '0.2rem 0.4rem' }}>{STEP_LABELS[st.step] || st.step}</td>
                                        <td style={{ padding: '0.2rem 0.4rem' }}><span style={statusBadge(st.status)}>{st.status}</span></td>
                                        <td style={{ padding: '0.2rem 0.4rem', color: 'var(--theme-text-secondary)' }}>{st.detail}</td>
                                        <td style={{ padding: '0.2rem 0.4rem', textAlign: 'right' }}>{st.duration_ms != null ? `${st.duration_ms}ms` : ''}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {/* Stats summary */}
                              <div>
                                <strong style={{ fontSize: '0.85rem' }}>Snapshot:</strong>
                                <div style={{ marginTop: '0.3rem', fontSize: '0.82rem', lineHeight: 1.7 }}>
                                  <div>Rooms: {s.rooms_occupied || 0} occupied / {s.rooms_vacant || 0} vacant / {s.rooms_ooo || 0} OOO</div>
                                  <div>Arrivals: {s.arrivals_expected || 0} exp / {s.arrivals_actual || 0} act</div>
                                  <div>Departures: {s.departures_expected || 0} exp / {s.departures_actual || 0} act</div>
                                  <div>Walk-Ins: {s.walk_ins || 0} | Stayovers: {s.stayovers || 0}</div>
                                  <div>Room Rev: {fmtNum(s.room_revenue)} | F&B: {fmtNum(s.fnb_revenue)} | Other: {fmtNum(s.other_revenue)}</div>
                                  <div>Total Rev: {fmtNum(s.total_revenue)} | Tax: {fmtNum(s.total_tax)} | Payments: {fmtNum(s.total_payments)}</div>
                                </div>
                              </div>
                            </div>
                            {h.error_message && (
                              <div style={{ marginTop: '0.5rem', color: 'var(--theme-danger)', fontSize: '0.82rem' }}>
                                <strong>Error:</strong> {h.error_message}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!history.length && !loading && (
                  <tr><td colSpan={10} style={{ padding: '1rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>No audit history found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* ── Email Report Tab ── */}
      {tab === 'email' && canManageEmail && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '1rem', maxWidth: 720 }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.3rem' }}>Night Audit Email Report</h3>
          <p style={{ marginTop: 0, color: 'var(--theme-text-secondary)', fontSize: '0.85rem' }}>
            After every Night Audit (manual or automatic) the full summary of the closed business date is emailed to the addresses below, with a PDF attached.
            Mail goes through the global SMTP account and only when the Platform Admin has enabled email for this property.
          </p>
          {emailErr && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{emailErr}</div>}
          {emailMsg && <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: '#dcfce7', color: '#166534', fontSize: '0.85rem' }}>{emailMsg}</div>}
          {!emailCfg ? <div style={{ color: 'var(--theme-text-secondary)' }}>Loading…</div> : (
            <div style={{ display: 'grid', gap: '0.8rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                <input type="checkbox" checked={emailForm.enabled} onChange={(e) => setEmailForm((f) => ({ ...f, enabled: e.target.checked }))} />
                Send Night Audit email for this property
              </label>
              <label style={{ display: 'grid', gap: '0.3rem', fontSize: '0.85rem', fontWeight: 600 }}>
                Recipient emails (one per line, or separated by commas)
                <textarea rows={4} value={emailForm.recipients} placeholder={'gm@hotel.com\nowner@hotel.com'}
                  onChange={(e) => setEmailForm((f) => ({ ...f, recipients: e.target.value }))}
                  style={{ padding: '0.5rem', borderRadius: 6, border: '1px solid var(--theme-border)', fontFamily: 'inherit', fontSize: '0.9rem', fontWeight: 400 }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem' }}>
                <input type="checkbox" checked={emailForm.send_on_failure} onChange={(e) => setEmailForm((f) => ({ ...f, send_on_failure: e.target.checked }))} />
                Also email an alert if Night Audit fails
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={saveEmailCfg} disabled={emailBusy}
                  style={{ padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: 'var(--theme-primary, #1e3a8a)', color: '#fff', fontWeight: 600, cursor: emailBusy ? 'wait' : 'pointer' }}>
                  {emailBusy ? 'Saving…' : 'Save'}
                </button>
                {emailCfg.last_sent_at && (
                  <span style={{ fontSize: '0.8rem', color: emailCfg.last_status === 'SENT' ? '#166534' : '#b91c1c' }}>
                    Last email {emailCfg.last_status === 'SENT' ? 'sent' : 'failed'} {fmtDt(emailCfg.last_sent_at)}{emailCfg.last_error ? ` — ${emailCfg.last_error}` : ''}
                  </span>
                )}
              </div>
              <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '0.8rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Send the report now for</span>
                <input type="date" value={emailSendDate} onChange={(e) => setEmailSendDate(e.target.value)}
                  style={{ padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid var(--theme-border)' }} />
                <button onClick={sendEmailNow} disabled={emailBusy || !emailSendDate}
                  style={{ padding: '0.45rem 1rem', borderRadius: 6, border: '1px solid var(--theme-border)', background: '#fff', fontWeight: 600, cursor: emailBusy ? 'wait' : 'pointer' }}>
                  Send now
                </button>
                <span style={{ color: 'var(--theme-text-secondary)' }}>Goes to the saved recipients — use it to test.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PmsNightAudit;
